import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from "react-router-dom";
import Checkbox from './Checkbox';
import CheckboxGroup from './CheckboxGroup';
import Radio from './Radio';
import RadioGroup from './RadioGroup';
import arts from './Arts';
import artImages from './artImages.json';
import tagCategories from './tagCategories.json';
import './Gallery.css';

// Edited with the Arts editor (npm run arts); tags in none of these lists are EXTRA.
const { type: typeTags, title: titleTags, character: characterTags } = tagCategories;

// [{name, count}] in first-seen order.
const countBy = (values) => {
    const counts = new Map();
    values.forEach(v => counts.set(v, (counts.get(v) || 0) + 1));
    return [...counts].map(([name, count]) => ({ name, count }));
};

// The art list is static, so the filter legend is built once.
const yearCounts = countBy(arts.map(art => art.year)).sort((a, b) => b.name - a.name);
const tagCounts = countBy(arts.flatMap(art => art.tag)).sort((a, b) => b.count - a.count);
const categorized = [...typeTags, ...titleTags, ...characterTags];
const tagSections = [
    { title: "TYPE", tags: tagCounts.filter(t => typeTags.includes(t.name)) },
    { title: "TITLE", tags: tagCounts.filter(t => titleTags.includes(t.name)) },
    { title: "CHARACTER", tags: tagCounts.filter(t => characterTags.includes(t.name)) },
    { title: "EXTRA", tags: tagCounts.filter(t => !categorized.includes(t.name)) },
];
// File names are not guaranteed unique, so list position is the React key.
const artKeys = new Map(arts.map((art, i) => [art, i]));

function GalleryList() {
    const ref = useRef(null);
    const [years, setYears] = useState([]);
    const [tags, setTags] = useState([]);
    const [sort, setSort] = useState("from newest");
    const [zoom, setZoom] = useState(-28);

    // Return to where the list was scrolled before opening an art.
    useEffect(() => {
        const scrollPosition = sessionStorage.getItem('scrollPosition');
        if (ref.current === null || scrollPosition === null) return;
        ref.current.scrollTo(0, parseInt(scrollPosition, 10));
    }, []);

    // Within a group any checked value matches; across groups both must match.
    const visibleArts = useMemo(() => {
        const filtered = arts.filter(art =>
            (years.length === 0 || years.includes(art.year)) &&
            (tags.length === 0 || tags.some(t => art.tag.includes(t))));
        return sort === "from oldest" ? [...filtered].reverse() : filtered;
    }, [years, tags, sort]);

    return (
        <div className='Gallery'>
            <div id="sidebar">
                <details>
                    <summary>Filter</summary>
                    <div id="filter" className="hide-scrollbar">
                        <CheckboxGroup label="YEAR" values={years} onChange={setYears}>
                            <hr />
                            <div className="legends">
                                {yearCounts.map(({ name, count }) => (
                                    <Checkbox key={name} value={name}>{`${name}(${count})`}</Checkbox>
                                ))}
                            </div>
                        </CheckboxGroup>

                        <CheckboxGroup label="TAG" values={tags} onChange={setTags}>
                            <hr />
                            <div className="legends">
                                {tagSections.map(section => (
                                    <div key={section.title}>
                                        <h1>{section.title}</h1>
                                        <div className="tags">
                                            {section.tags.map(({ name, count }) => (
                                                <Checkbox key={name} value={name}>{`${name} (${count})`}</Checkbox>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CheckboxGroup>
                    </div>
                    <hr />
                    <div id="sort">
                        <RadioGroup label="Sort" value={sort} onChange={setSort}>
                            <Radio name='sort' value='from newest'>
                                <span>FROM NEWEST</span>
                            </Radio>
                            <Radio name='sort' value='from oldest'>
                                <span>FROM OLDEST</span>
                            </Radio>
                        </RadioGroup>
                    </div>
                    <hr id="zoom_line"/>
                    <div id="zoom_button">
                        <button className="Smaller" type="button" onClick={() => setZoom(zoom - 20)}>－</button>
                        <button className="Bigger" type="button" onClick={() => setZoom(zoom + 20)}>＋</button>
                    </div>
                </details>
            </div>
            <div className="gallery_list hide-scrollbar" ref={ref}>
                {visibleArts.map(art => (
                    <div key={artKeys.get(art)}>
                        <Link to={`/Gallery/${art.fileName}`}
                            onClick={() => sessionStorage.setItem('scrollPosition', ref.current.scrollTop)}>
                            <img src={`/Art-thumb/${artImages[art.fileName].thumb}`} alt={art.comment}
                                width={artImages[art.fileName].width} height={artImages[art.fileName].height}
                                loading="lazy" decoding="async"
                                style={{height: `calc(25vw + ${zoom}px)`}}/>
                        </Link>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default GalleryList;
